// Local notifications for the native app. All calls are no-ops on web.
// Two features: (1) a gentle daily "pick today's one thing" reminder,
// (2) optional per-time-block reminders 5 minutes before each block.
// Per-block reminder preferences are stored on-device (Preferences), keyed by
// date, because time_blocks on the server only carry {time,label}.
import { isNative, kvGet, kvSet, kvRemove } from "@/lib/native";

const DAILY_IDS = [10001, 10002, 10003, 10004, 10005, 10006, 10007]; // one per weekday
const BLOCK_ID_BASE = 20000; // block index i -> BLOCK_ID_BASE + i
const DAILY_PREF = "cc_daily_reminder"; // JSON {enabled, hour, minute}
const blockPrefKey = (date) => `cc_block_reminders_${date}`;

// Calm, no-guilt, no-streak reminder copy. One per weekday so it rotates across the week.
const DAILY_MESSAGES = [
  "What is the one thing worth protecting time for today?",
  "Gentle nudge: pick today's one thing whenever you are ready.",
  "A single priority is plenty. What is yours today?",
  "No pressure today, just choose one thing to aim at.",
  "Open when you can. What matters most right now?",
  "One small win still counts. What could today's be?",
  "Your day, one screen. Pick a place to start.",
];

const LN = async () => (await import("@capacitor/local-notifications")).LocalNotifications;

export const getDailyPref = async () => {
  const raw = await kvGet(DAILY_PREF);
  if (!raw) return { enabled: false, hour: 8, minute: 30 };
  try {
    const p = JSON.parse(raw);
    return { enabled: !!p.enabled, hour: p.hour ?? 8, minute: p.minute ?? 30 };
  } catch (e) {
    return { enabled: false, hour: 8, minute: 30 };
  }
};

const saveDailyPref = (p) => kvSet(DAILY_PREF, JSON.stringify(p));

// Returns "granted" | "denied" | "prompt"
export const checkPermission = async () => {
  if (!isNative()) return "denied";
  try {
    const res = await (await LN()).checkPermissions();
    return res.display;
  } catch (e) {
    return "denied";
  }
};

export const requestPermission = async () => {
  if (!isNative()) return "denied";
  try {
    const res = await (await LN()).requestPermissions();
    return res.display;
  } catch (e) {
    return "denied";
  }
};

export const enableDailyReminder = async (hour, minute) => {
  if (!isNative()) return false;
  const ln = await LN();
  await ln.cancel({ notifications: DAILY_IDS.map((id) => ({ id })) }).catch(() => {});
  // Schedule one repeating notification per weekday (1=Sunday..7=Saturday) so the
  // copy rotates through the week. Each repeats weekly at the chosen time.
  const notifications = DAILY_IDS.map((id, i) => ({
    id,
    title: "Command Center",
    body: DAILY_MESSAGES[i % DAILY_MESSAGES.length],
    schedule: { on: { weekday: i + 1, hour, minute }, allowWhileIdle: true },
  }));
  await ln.schedule({ notifications });
  await saveDailyPref({ enabled: true, hour, minute });
  return true;
};

export const disableDailyReminder = async () => {
  const p = await getDailyPref();
  await saveDailyPref({ ...p, enabled: false });
  if (!isNative()) return;
  const ln = await LN();
  await ln.cancel({ notifications: DAILY_IDS.map((id) => ({ id })) }).catch(() => {});
};

// ---- Per-time-block reminders ----
export const getBlockReminderSet = async (date) => {
  const raw = await kvGet(blockPrefKey(date));
  if (!raw) return {};
  try {
    return JSON.parse(raw) || {};
  } catch (e) {
    return {};
  }
};

const saveBlockReminderSet = (date, set) => {
  const keys = Object.keys(set).filter((k) => set[k]);
  if (keys.length === 0) return kvRemove(blockPrefKey(date));
  const clean = {};
  keys.forEach((k) => (clean[k] = true));
  return kvSet(blockPrefKey(date), JSON.stringify(clean));
};

// Lenient parser for free-text times like "9am", "9:30 am", "2pm", "14:00", "9".
export const parseBlockTime = (str, date) => {
  const s = (str || "").trim().toLowerCase();
  if (!s) return null;
  const m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/);
  if (!m) return null;
  let hour = parseInt(m[1], 10);
  const minute = m[2] ? parseInt(m[2], 10) : 0;
  const ampm = m[3];
  if (hour > 23 || minute > 59) return null;
  if (ampm === "pm" && hour < 12) hour += 12;
  if (ampm === "am" && hour === 12) hour = 0;
  const [y, mo, d] = date.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(y, mo - 1, d, hour, minute, 0, 0);
  return dt;
};

// Cancel and re-schedule reminders for all currently-enabled blocks on `date`.
const reschedule = async (date, blocks, set) => {
  if (!isNative()) return;
  const ln = await LN();
  const idsToClear = (blocks || []).map((_, i) => ({ id: BLOCK_ID_BASE + i }));
  // also clear a generous range in case blocks were removed
  for (let i = 0; i < 20; i++) idsToClear.push({ id: BLOCK_ID_BASE + i });
  await ln.cancel({ notifications: idsToClear }).catch(() => {});
  const now = Date.now();
  const toSchedule = [];
  (blocks || []).forEach((b, i) => {
    if (!set[i]) return;
    const dt = parseBlockTime(b.time, date);
    if (!dt) return;
    const at = new Date(dt.getTime() - 5 * 60 * 1000); // 5 min before
    if (at.getTime() <= now) return; // don't schedule past times
    toSchedule.push({
      id: BLOCK_ID_BASE + i,
      title: "Coming up in 5 minutes",
      body: (b.label || "Time block").slice(0, 120),
      schedule: { at, allowWhileIdle: true },
    });
  });
  if (toSchedule.length) await ln.schedule({ notifications: toSchedule });
};

export const toggleBlockReminder = async (date, index, on, blocks) => {
  const set = await getBlockReminderSet(date);
  if (on) set[index] = true;
  else delete set[index];
  await saveBlockReminderSet(date, set);
  await reschedule(date, blocks, set);
  return set;
};

// Call whenever blocks change / day resets so schedules stay in sync.
export const syncBlockReminders = async (date, blocks) => {
  if (!isNative()) return;
  const set = await getBlockReminderSet(date);
  await reschedule(date, blocks, set);
};

export const clearBlockReminders = async (date) => {
  await kvRemove(blockPrefKey(date));
  if (!isNative()) return;
  const ln = await LN();
  const ids = [];
  for (let i = 0; i < 20; i++) ids.push({ id: BLOCK_ID_BASE + i });
  await ln.cancel({ notifications: ids }).catch(() => {});
};
